import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';

/**
 * OwnerBootstrap — one-time owner account creation.
 *
 * Shown only when the server has an outstanding bootstrap token AND no admin
 * account exists. The token itself must be generated server-side via the
 * `owner_bootstrap.py generate` CLI and pasted into this form by the operator.
 *
 * On success the operator is issued a session cookie and forwarded into /app.
 */
export default function OwnerBootstrap() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [available, setAvailable] = useState(null); // null | true | false
  const [token, setToken] = useState(params.get('token') || '');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get('/auth/bootstrap-status');
        if (!cancelled) setAvailable(!!r.data?.bootstrap_available);
      } catch { if (!cancelled) setAvailable(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    if (password.length < 12) { toast.error('Owner password must be at least 12 characters.'); return; }
    setLoading(true);
    try {
      await http.post('/auth/bootstrap-owner', { token: token.trim(), email: email.trim().toLowerCase(), name: name.trim(), password });
      toast.success('Owner account created. Welcome.');
      if (refresh) await refresh();
      nav('/app');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Bootstrap failed. Check your token.');
    } finally {
      setLoading(false);
    }
  };

  if (available === null) {
    return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-navy/50">Checking…</div>;
  }

  if (available === false) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 card-soft text-center" data-testid="bootstrap-unavailable">
          <ShieldCheck size={32} className="mx-auto text-navy/40" strokeWidth={1.5}/>
          <h1 className="mt-4 font-heading text-2xl">Bootstrap unavailable</h1>
          <p className="text-navy/60 text-sm mt-3">Owner bootstrap is not currently active. If you need to set up an owner account, run the server-side generate command first.</p>
          <Link to="/" className="mt-6 inline-block text-sm text-navy/70 hover:text-navy underline">← Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="bootstrap-form-wrap">
        <Link to="/" className="text-sm text-navy/60 hover:text-navy">← Home</Link>
        <div className="mt-6 bg-white rounded-3xl p-8 card-soft">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-2xl bg-gold/15 text-gold flex items-center justify-center">
              <ShieldCheck size={20}/>
            </div>
            <p className="text-[11px] uppercase tracking-widest text-gold font-semibold">Owner bootstrap</p>
          </div>
          <h1 className="font-heading text-3xl mt-1">Create the owner account</h1>
          <p className="text-navy/60 text-sm mt-2 mb-6">One-time setup. This form disappears the moment the first owner is created.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Bootstrap token</label>
              <input data-testid="bootstrap-token" required autoFocus value={token} onChange={(e)=>setToken(e.target.value)}
                placeholder="Paste the token from the server console"
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Owner name</label>
              <input data-testid="bootstrap-name" required value={name} onChange={(e)=>setName(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Email</label>
              <input data-testid="bootstrap-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Password</label>
              <input data-testid="bootstrap-password" type="password" required minLength={12} value={password} onChange={(e)=>setPassword(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
              <p className="mt-1 text-[11px] text-navy/45">Minimum 12 characters. Use a passphrase — this account has full access.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-navy/60">Confirm password</label>
              <input data-testid="bootstrap-password-confirm" type="password" required minLength={12} value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                className="mt-1 w-full bg-[#F9F8F6] border border-navy/10 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gold" />
            </div>
            <button data-testid="bootstrap-submit" disabled={loading}
              className="w-full bg-navy text-white rounded-full py-4 font-medium hover:bg-navy-soft transition-smooth disabled:opacity-50">
              {loading ? 'Creating owner…' : 'Create owner account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
